import React, { useMemo, useState } from 'react';
import { ArrowLeft, Copy, DoorOpen, Link2, LogOut, MoreVertical, Plus, RefreshCw, ShieldCheck, ShieldOff, UserMinus, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useInvalidateLineRead, useLineActionCall, useLineRead, type Obj } from '@/client/api/whatsapp/line-actions';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  ActionButton,
  areaCls,
  asArray,
  ConfirmDialog,
  copyText,
  digitsOf,
  Empty,
  ErrorNote,
  Loading,
  readAsBase64,
  Section,
  selectCls,
  toastError,
  useActionAccess,
} from './shared';

type Ask = { title: string; body: string; action: string; run: () => void };

const CreateDialog: React.FC<{ instance: string; open: boolean; onClose: () => void; onDone: () => void }> = ({ instance, open, onClose, onDone }) => {
  const call = useLineActionCall();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [people, setPeople] = useState('');
  const numbers = people.split(/[\n,]/).map(digitsOf).filter((n) => n.length >= 8);
  const submit = () =>
    call.mutate(
      { instance, action: 'group.create', body: { subject: subject.trim(), description: description.trim() || undefined, participants: numbers } },
      {
        onSuccess: () => {
          toast.success('Group created');
          setSubject(''); setDescription(''); setPeople('');
          onDone();
        },
        onError: (e) => toastError(e, 'Could not create the group'),
      },
    );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !call.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create group</DialogTitle>
          <DialogDescription>The linked phone becomes the group admin. The people you add are notified.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
          <div><Label>Description (optional)</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div>
            <Label>Members, one phone per line (country code first)</Label>
            <textarea className={areaCls} rows={4} value={people} onChange={(e) => setPeople(e.target.value)} />
            <p className="text-xs text-gray-500">{numbers.length} valid number{numbers.length === 1 ? '' : 's'}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={call.isPending}>Cancel</Button>
          <Button onClick={submit} disabled={call.isPending || !subject.trim() || numbers.length === 0}>{call.isPending ? 'Creating...' : 'Create group'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const GroupDetail: React.FC<{ instance: string; jid: string; onBack: () => void }> = ({ instance, jid, onBack }) => {
  const call = useLineActionCall();
  const invalidate = useInvalidateLineRead();
  const { can } = useActionAccess();
  const info = useLineRead<Obj>(instance, 'group.findGroupInfos', { query: { groupJid: jid } });
  const invite = useLineRead<Obj>(instance, 'group.inviteCode', { query: { groupJid: jid } });
  const g = info.data;
  const [subject, setSubject] = useState<string | null>(null);
  const [desc, setDesc] = useState<string | null>(null);
  const [picture, setPicture] = useState('');
  const [add, setAdd] = useState('');
  const [sendTo, setSendTo] = useState('');
  const [ask, setAsk] = useState<Ask | null>(null);

  const run = (action: string, body: Obj, done: string, after?: () => void) =>
    call.mutate(
      { instance, action, body: { groupJid: jid, ...body } },
      {
        onSuccess: () => {
          toast.success(done);
          setAsk(null);
          after?.();
          invalidate(instance, 'group.findGroupInfos');
          invalidate(instance, 'group.inviteCode');
          invalidate(instance, 'group.fetchAllGroups');
        },
        onError: (e) => { setAsk(null); toastError(e, 'The action failed'); },
      },
    );
  const confirmRun = (a: Ask) => setAsk(a);
  const numbers = (s: string) => s.split(/[\n,]/).map(digitsOf).filter((n) => n.length >= 8);

  const setting = (label: string, action: string, key: string, options: [string, string][], current?: string) => (
    <div>
      <Label>{label}</Label>
      <select
        className={selectCls}
        value={current ?? ''}
        disabled={!can(action)}
        onChange={(e) => {
          const v = e.target.value;
          const text = options.find((o) => o[0] === v)?.[1] ?? v;
          confirmRun({ title: `Change ${label.toLowerCase()}?`, body: `Set to "${text}" for everyone in the group.`, action: 'Change', run: () => run(action, { [key]: key === 'expiration' ? Number(v) : v }, `${label} updated`) });
        }}
      >
        {current === undefined && <option value="">Select</option>}
        {options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </div>
  );

  if (info.isLoading) return <Loading what="the group" />;
  if (info.isError) return <div className="space-y-2"><button onClick={onBack} className="text-sm text-gray-600 underline">Back to groups</button><ErrorNote error={info.error} what="the group" /></div>;
  const participants = asArray<Obj>(g?.participants);

  return (
    <div className="space-y-6">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"><ArrowLeft className="h-4 w-4" /> All groups</button>
      <div className="flex items-center gap-3">
        {g?.pictureUrl ? <img src={g.pictureUrl} alt="" className="h-12 w-12 rounded-full object-cover" /> : <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"><Users className="h-5 w-5 text-gray-400" /></div>}
        <div>
          <div className="font-semibold text-gray-900">{g?.subject}</div>
          <div className="font-mono text-xs text-gray-500">{jid}</div>
        </div>
      </div>

      <Section title="Details">
        <div className="space-y-3">
          <div>
            <Label>Subject</Label>
            <div className="flex gap-2">
              <Input value={subject ?? g?.subject ?? ''} onChange={(e) => setSubject(e.target.value)} />
              <ActionButton action="group.updateGroupSubject" disabled={subject === null || !subject.trim() || subject === g?.subject} onClick={() => run('group.updateGroupSubject', { subject }, 'Subject updated', () => setSubject(null))}>Save</ActionButton>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <textarea className={areaCls} rows={3} value={desc ?? g?.desc ?? ''} onChange={(e) => setDesc(e.target.value)} />
            <ActionButton action="group.updateGroupDescription" className="mt-2" disabled={desc === null || desc === (g?.desc ?? '')} onClick={() => run('group.updateGroupDescription', { description: desc }, 'Description updated', () => setDesc(null))}>Save description</ActionButton>
          </div>
          <div>
            <Label>Picture (https URL, or choose a file)</Label>
            <div className="flex gap-2">
              <Input placeholder="https://" value={picture} onChange={(e) => setPicture(e.target.value)} />
              <ActionButton action="group.updateGroupPicture" disabled={!/^https:\/\//i.test(picture)} onClick={() => run('group.updateGroupPicture', { image: picture.trim() }, 'Picture updated', () => setPicture(''))}>Set</ActionButton>
            </div>
            <input type="file" accept="image/*" className="mt-2 text-xs" disabled={!can('group.updateGroupPicture')} onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try { run('group.updateGroupPicture', { image: await readAsBase64(f) }, 'Picture updated'); } catch (err) { toastError(err, 'Could not read the file'); }
              e.target.value = '';
            }} />
          </div>
        </div>
      </Section>

      <Section title={`Participants (${participants.length})`}>
        <ul className="divide-y rounded-lg border border-gray-200 bg-white">
          {participants.map((p) => {
            const num = String(p.id ?? p.phoneNumber ?? '').split('@')[0];
            const isAdmin = !!p.admin;
            const label = p.phoneNumber ? `+${String(p.phoneNumber).split('@')[0]}` : `+${num}`;
            const act = (a: 'remove' | 'promote' | 'demote', title: string, body: string) =>
              confirmRun({ title, body, action: title.replace('?', ''), run: () => run('group.updateParticipant', { action: a, participants: [num] }, 'Participant updated') });
            return (
              <li key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span>{label} {isAdmin && <span className="ml-1 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">{p.admin === 'superadmin' ? 'Owner' : 'Admin'}</span>}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><button type="button" aria-label={`Actions for ${label}`} className="rounded p-1.5 hover:bg-gray-100"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {isAdmin ? (
                      <DropdownMenuItem disabled={!can('group.updateParticipant')} onClick={() => act('demote', 'Demote to member?', `${label} will lose admin rights in this group.`)}><ShieldOff className="mr-2 h-4 w-4" /> Demote</DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem disabled={!can('group.updateParticipant')} onClick={() => act('promote', 'Make admin?', `${label} will be able to change the group and remove members.`)}><ShieldCheck className="mr-2 h-4 w-4" /> Promote to admin</DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-red-600" disabled={!can('group.updateParticipant')} onClick={() => act('remove', 'Remove from group?', `${label} will be removed from the group.`)}><UserMinus className="mr-2 h-4 w-4" /> Remove</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            );
          })}
        </ul>
        <div className="flex gap-2">
          <Input placeholder="Add phones, comma separated" value={add} onChange={(e) => setAdd(e.target.value)} />
          <ActionButton action="group.updateParticipant" disabled={!numbers(add).length} onClick={() => confirmRun({ title: 'Add to group?', body: `${numbers(add).length} number(s) will be added and notified.`, action: 'Add', run: () => run('group.updateParticipant', { action: 'add', participants: numbers(add) }, 'Participants added', () => setAdd('')) })}><UserPlus /> Add</ActionButton>
        </div>
      </Section>

      <Section title="Invite link">
        {invite.isLoading && <Loading what="the invite link" />}
        {invite.isError && <p className="text-sm text-gray-500">No invite link available. This line may not be an admin of the group.</p>}
        {invite.data?.inviteUrl && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-gray-50 px-2 py-1.5 text-xs">{invite.data.inviteUrl}</code>
              <Button variant="outline" size="sm" onClick={() => copyText(invite.data!.inviteUrl, 'Invite link')}><Copy /> Copy</Button>
              <ActionButton action="group.revokeInviteCode" variant="outline" size="sm" onClick={() => confirmRun({ title: 'Revoke the invite link?', body: 'The current link stops working and a new one is created. Anyone holding the old link can no longer join.', action: 'Revoke', run: () => run('group.revokeInviteCode', {}, 'Invite link revoked') })}><RefreshCw /> Revoke</ActionButton>
            </div>
            <div className="flex gap-2">
              <Input placeholder="Send the link to phones, comma separated" value={sendTo} onChange={(e) => setSendTo(e.target.value)} />
              <ActionButton action="group.sendInvite" disabled={!numbers(sendTo).length} onClick={() => confirmRun({ title: 'Send the invite?', body: `The link is sent as a WhatsApp message to ${numbers(sendTo).length} number(s).`, action: 'Send', run: () => run('group.sendInvite', { description: `Join ${g?.subject ?? 'our group'}`, numbers: numbers(sendTo) }, 'Invite sent', () => setSendTo('')) })}><Link2 /> Send</ActionButton>
            </div>
          </div>
        )}
      </Section>

      <Section title="Settings">
        <div className="grid gap-3 sm:grid-cols-2">
          {setting('Who can send messages', 'group.updateSetting', 'action', [['not_announcement', 'Everyone'], ['announcement', 'Only admins']], g?.announce ? 'announcement' : 'not_announcement')}
          {setting('Who can edit group info', 'group.updateSetting', 'action', [['unlocked', 'Everyone'], ['locked', 'Only admins']], g?.restrict ? 'locked' : 'unlocked')}
          {setting('Who can add members', 'group.updateMemberAddMode', 'mode', [['all_member_add', 'Everyone'], ['admin_add', 'Only admins']])}
          {setting('Disappearing messages', 'group.toggleEphemeral', 'expiration', [['0', 'Off'], ['86400', '24 hours'], ['604800', '7 days'], ['7776000', '90 days']])}
        </div>
      </Section>

      <Section title="Leave group">
        <ActionButton action="group.leaveGroup" variant="outline" className="text-red-600" onClick={() => confirmRun({ title: 'Leave this group?', body: 'The linked phone leaves the group. To come back it must be re-invited.', action: 'Leave group', run: () => call.mutate({ instance, action: 'group.leaveGroup', query: { groupJid: jid } }, { onSuccess: () => { toast.success('Left the group'); invalidate(instance, 'group.fetchAllGroups'); onBack(); }, onError: (e) => { setAsk(null); toastError(e, 'Could not leave the group'); } }) })}><LogOut /> Leave group</ActionButton>
      </Section>

      <ConfirmDialog open={!!ask} title={ask?.title ?? ''} body={ask?.body} action={ask?.action ?? ''} pending={call.isPending} onConfirm={() => ask?.run()} onClose={() => setAsk(null)} />
    </div>
  );
};

const GroupsTab: React.FC<{ instance: string; connected: boolean }> = ({ instance, connected }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const invalidate = useInvalidateLineRead();
  const q = useLineRead<unknown>(instance, 'group.fetchAllGroups', { query: { getParticipants: 'false' }, enabled: connected });
  const groups = asArray<Obj>(q.data);
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return groups.filter((g) => !s || `${g.subject ?? ''} ${g.id}`.toLowerCase().includes(s));
  }, [groups, search]);

  if (!connected) return <Empty title="No phone is linked" hint="Link a phone from the Overview tab to manage its groups." />;
  if (open) return <GroupDetail instance={instance} jid={open} onBack={() => setOpen(null)} />;

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input placeholder="Search groups by name" value={search} onChange={(e) => setSearch(e.target.value)} />
        <ActionButton action="group.create" onClick={() => setCreateOpen(true)}><Plus /> Create</ActionButton>
      </div>
      {q.isLoading && <Loading what="groups" />}
      {q.isError && <ErrorNote error={q.error} what="groups" />}
      {q.isSuccess && shown.length === 0 && <Empty title={search ? 'No group matches that search' : 'This line is in no groups'} hint={search ? 'Clear the search to see every group.' : 'Create a group to get started.'} />}
      {shown.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500"><tr><th className="px-3 py-2">Group</th><th className="px-3 py-2">Members</th><th className="px-3 py-2" /></tr></thead>
            <tbody className="divide-y">
              {shown.map((g) => (
                <tr key={g.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setOpen(g.id)}>
                  <td className="px-3 py-2"><div className="font-medium text-gray-900">{g.subject}</div><div className="font-mono text-xs text-gray-500">{g.id}</div></td>
                  <td className="px-3 py-2 text-gray-700">{g.size}</td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button type="button" aria-label={`Actions for ${g.subject}`} className="rounded p-1.5 hover:bg-gray-100"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setOpen(g.id)}><DoorOpen className="mr-2 h-4 w-4" /> Open</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => copyText(g.id, 'Group id')}><Copy className="mr-2 h-4 w-4" /> Copy group id</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CreateDialog instance={instance} open={createOpen} onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); invalidate(instance, 'group.fetchAllGroups'); }} />
    </div>
  );
};

export default GroupsTab;
